import 'highlight.js/styles/monokai-sublime.css';
import 'react-quill/dist/quill.bubble.css';

import { Loading } from '@descomplica/foundation-ui-components';
import { observer, useViewModel } from '@descomplica/microfrontend-react';
import React, { createRef } from 'react';
import ReactQuill from 'react-quill';
import styled from 'styled-components';

import { ExamViewModel } from '../../exam.vm';
import Correct from '../icons/Correct.svg';
import Wrong from '../icons/Wrong.svg';

const Alternative = styled.div<{ answered: boolean; correctChoice: boolean; checked: boolean }>`
  font-family: 'Aprova Nova';
  cursor: pointer;
  font-style: normal;
  font-weight: 400;
  font-size: 20px;
  line-height: 32px;
  display: flex;
  align-items: flex-end;
  letter-spacing: -0.8px;
  color: #666666;
  padding: 20px 3px;
  width: 100%;
  cursor: pointer;
  border: 2px solid transparent;

  &:hover {
    border: ${(props) => {
      if (!props.checked) return '2px solid #111';

      if (props.checked) return '2px solid transparent';
    }};
    border-radius: 8px;
    padding: 20px 3px;
    width: 98%;
  }

  label {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    cursor: pointer;
  }

  input[type='radio'] {
    visibility: hidden;
  }

  input[type='radio']:checked + label {
    color: #111;
    border: 2px solid
      ${(props) => {
        if (props.answered === null) return '#111111';

        if (props.answered) return '#00c974';

        if (!props.answered && props.correctChoice) return '#00c974';

        if (!props.answered && !props.correctChoice) return '#DB3D4D';
      }};
    border-radius: 8px;
    padding: 20px 3px;
    width: 98%;
  }

  & > label {
    span {
      color: #666666;
      padding-right: 5px;
    }
  }

  .ql-container {
    font-style: normal;
    font-weight: 400;
    font-size: 18px;
    line-height: 32px;
    letter-spacing: -0.8px;
    color: #333333;
  }

  .ql-editor > * {
    font-family: Aprova Nova;
    cursor: pointer !important;
  }
`;

const QuestionStatement = styled.h2`
  margin-top: 10px;
  width: 100%;

  .ql-container {
    font-style: normal;
    font-weight: 400;
    font-size: 18px;
    line-height: 32px;
    letter-spacing: -0.8px;
    color: #333333;
  }

  .ql-editor {
    padding: 0;
    font-family: Aprova Nova;
  }
`;

const QuestionContainer = styled.div<{ answered: boolean; isResult: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  width: ${(props) => (props.isResult ? '70%' : '100%')};
  pointer-events: ${(props) => (props.answered ? 'none' : 'auto')};
  cursor: ${(props) => (props.answered ? 'not-allowed' : 'auto')};
`;

const QuestionsContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
`;

const SolutionContainer = styled.div`
  display: flex;
  padding: 40px 48px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  flex: 1 0 0;
  align-self: stretch;
  background: #f2f2f3;
  margin: -86px -48px -48px auto;
`;

const SolutionTitle = styled.h2`
  color: #191a1a;
  font-family: Aprova Nova;
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 28px;
  letter-spacing: -1px;
  width: 100%;
  text-align: start;
  padding-left: 1rem;
`;

const alternativesAlphabet = 'abcdefghijklmnopqrstuvwxyz';

export const Questions: React.FC<any> = ({ question }) => {
  const vm = useViewModel(ExamViewModel);

  let inputRefs = React.useRef([]);

  inputRefs = question?.choices?.map((_, i) => inputRefs[i] ?? createRef());

  const handleOptionChange = async (event) => {
    vm.setSelectedOption(event.target.value);
  };

  const handleButtonOptionChange = async (event) => {
    vm.setSelectedOption(event.value);
  };

  const handleIndicator = (alt, question) => {
    if (question.answered) {
      if (alt.guid === question.correct_choice) return <img src={Correct} alt="" />;

      if (alt.guid !== question.correct_choice) return <img src={Wrong} alt="" />;

      return <img src={Correct} alt="" />;
    }
  };

  const handleChecked = (question, alt) => {
    return (
      question.choices_guid === alt.guid ||
      alt.guid === question.correct_choice ||
      vm.selectedOption === alt.guid
    );
  };

  if (!question) {
    return <Loading />;
  }

  const currentCorrectChoice =
    question.choices.length > 0 && question?.choices?.find((choice) => choice.justify);

  const currentQuestionCheck =
    vm.selectedOption ??
    vm.answers?.answers.find((answer) => answer.question_guid === question.question_guid);

  return (
    <QuestionsContainer>
      <QuestionContainer
        answered={question.answered}
        isResult={question.answered && currentCorrectChoice?.justify}
      >
        <QuestionStatement>
          <ReactQuill theme="bubble" value={question.question_statement} readOnly={true} />
        </QuestionStatement>
        <div className="alternatives">
          {question.choices.map((alt, altIndex) => {
            return (
              <Alternative
                key={altIndex}
                answered={question.answered ? question.correct : null}
                correctChoice={alt.guid === question.correct_choice}
                checked={
                  question.choices_guid === alt.guid ||
                  alt.guid === question.correct_choice ||
                  vm.selectedOption === alt.guid ||
                  (!!currentQuestionCheck && alt.guid === currentQuestionCheck.choices_guid)
                }
                onClick={() => handleButtonOptionChange(inputRefs[altIndex].current)}
              >
                <input
                  id={alt.guid}
                  type="radio"
                  value={alt.guid}
                  checked={
                    question.choices_guid === alt.guid ||
                    alt.guid === question.correct_choice ||
                    vm.selectedOption === alt.guid ||
                    (!!currentQuestionCheck && alt.guid === currentQuestionCheck.choices_guid)
                  }
                  onChange={handleOptionChange}
                  ref={inputRefs[altIndex]}
                />
                <label htmlFor={alt.guid}>
                  <span>
                    {handleChecked(question, alt)
                      ? handleIndicator(alt, question)
                      : `${alternativesAlphabet.substring(altIndex, altIndex + 1)}. `}
                  </span>
                  <ReactQuill theme="bubble" value={alt.text} readOnly={true} />
                </label>
              </Alternative>
            );
          })}
        </div>
      </QuestionContainer>
      {question.answered && currentCorrectChoice?.justify && (
        <SolutionContainer>
          <SolutionTitle>Solução</SolutionTitle>
          <ReactQuill theme="bubble" value={currentCorrectChoice?.justify} readOnly={true} />
        </SolutionContainer>
      )}
    </QuestionsContainer>
  );
};

export default observer(Questions);
